import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useCallback } from 'react';
import VoxelIconButton from '../../../../../../../../../voxel/Buttons/VoxelIconButton';
import VoxelInput from '../../../../../../../../../voxel/Forms/VoxelInput';
import VoxelTextarea from '../../../../../../../../../voxel/Forms/VoxelTextArea';
import { replaceTabsWithLinebreaks } from '../../../../../../../../../../logic/otherUtilities/replaceTabsWithLinebreaks';
import { getCombinationName } from '../../logic/getCombinationName';
import { useRemoveGroup } from '../../logic/stateActions/removeGroup';
import { FormWordGroup } from '../../models/ComboFormState';
import { SetFormState } from '../../models/SetFormState';

export interface WordGroupListItemProps {
  disabled: boolean;
  wordGroup: FormWordGroup;
  setFormState: SetFormState;
  index: number;
}

const WordGroupListItem: React.FC<WordGroupListItemProps> = React.memo(
  ({ wordGroup, disabled, setFormState, index }) => {
    const handleUpdateGroupWords = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = replaceTabsWithLinebreaks(event.currentTarget.value);
        setFormState((prevState) => ({
          ...prevState,
          wordGroups: prevState.wordGroups.map((wg) => {
            if (wg.id === wordGroup.id) {
              return { ...wg, words: value };
            }
            return wg;
          }),
        }));
      },
      [setFormState, wordGroup.id, wordGroup.words]
    );

    const handleUpdateGroupName = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        // There's a slight hack below to add/remove a group to update all the combionation names
        const value = replaceTabsWithLinebreaks(event.currentTarget.value);
        setFormState((prevState) => {
          const nextState = {
            ...prevState,
          };

          // Update word group name
          nextState.wordGroups = prevState.wordGroups.map((wg) => {
            if (wg.id === wordGroup.id) {
              return { ...wg, name: value };
            }
            return wg;
          });

          // Update combination names (needed because the names are searchable etc.)
          nextState.comboCombinations = prevState.comboCombinations.map((combination) => {
            if (combination.wordGroups.some((wg) => wg.id === wordGroup.id)) {
              return {
                ...combination,
                name: getCombinationName(combination, nextState.wordGroups),
              };
            }
            return combination;
          });

          return nextState;
        });
      },
      [setFormState, wordGroup.id]
    );

    const handleRemoveGroup = useRemoveGroup(setFormState, wordGroup.id);

    return (
      <div className="flex flex-col gap-4 bg-gray-100 p-6 rounded-lg w-72 shrink-0">
        <div className="flex flex-row items-center gap-4">
          <div className="flex-grow">
            <VoxelInput
              onChange={handleUpdateGroupName}
              label="Group"
              labelPosition="top"
              value={wordGroup.name}
              size="sm"
              placeholder="Word group name"
              helperText={!wordGroup.name?.length ? 'Name required' : ''}
              disabled={disabled}
            />
          </div>
          <div>
            <div className="h-6" />
            <VoxelIconButton
              tooltip="Remove group"
              disabled={disabled}
              onClick={handleRemoveGroup}
              icon={<FontAwesomeIcon icon={faTimes} />}
            />
          </div>
        </div>
        <VoxelTextarea
          label="Words"
          value={wordGroup.words}
          onChange={(e: any) => handleUpdateGroupWords(e)}
          disabled={disabled}
          minRows={10}
        />
      </div>
    );
  }
);

export default WordGroupListItem;
